import { isAxiosError } from 'axios'
import type { UnwrapRef } from 'vue'

export default function useRequest<Params extends unknown[], ResponseData>(
  requestFn: (...args: Params) => Promise<ResponseData>
) {
  const successful = ref(false)
  const pending = ref(false)
  const error = ref<unknown>(null)
  const response = ref<ResponseData | null>(null)

  const { t } = useI18n()
  const errorMessage = computed(() => {
    if (!error.value) return ''
    if (isAxiosError(error.value)) {
      const message = error.value.response?.data?.detail
      if (typeof message === 'string') return message
    }
    return t('form.something-went-wrong-try-again')
  })

  const execute = async (...args: Params) => {
    pending.value = true
    response.value = null
    error.value = null
    successful.value = false

    try {
      response.value = (await requestFn(...args)) as UnwrapRef<ResponseData>
      successful.value = true
    } catch (e) {
      error.value = e
    }

    pending.value = false
  }

  const clear = () => {
    pending.value = false
    response.value = null
    error.value = null
    successful.value = false
  }

  return {
    pending,
    execute,
    error,
    errorMessage,
    successful,
    clear
  }
}
